import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { fmtTime, todayLocalIso } from '../../shared/dates';
import { Icon } from '../../ui/Icon';
import { GroupForm } from './GroupForm';

export function GroupsPage() {
  const qc = useQueryClient();
  const gridRef = useRef<AgGridReact | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['groups', showInactive],
    queryFn: () =>
      api
        .get('/group-sessions', { params: showInactive ? {} : { activeOnly: 'true' } })
        .then((r) => r.data),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) =>
      api.patch(`/group-sessions/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Group updated');
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.delete(`/group-sessions/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Group deactivated');
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/group-sessions/${id}`, { active: true }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Group reactivated');
    },
  });

  const cols = useMemo<ColDef[]>(
    () => [
      { headerName: 'Display name', field: 'displayName', pinned: 'left', editable: true, width: 200 },
      { headerName: 'Type', field: 'serviceType', width: 140 },
      { headerName: 'Days', valueGetter: (p) => (p.data?.days ?? []).join(', '), width: 180 },
      {
        headerName: 'Time',
        valueGetter: (p) => (p.data ? `${fmtTime(p.data.startMin)}–${fmtTime(p.data.endMin)}` : ''),
        width: 170,
      },
      { headerName: 'Place', field: 'placeOfService', width: 140 },
      { headerName: 'Max', field: 'maxClients', editable: true, width: 80 },
      { headerName: 'Enrolled', field: 'enrollmentCount', width: 100 },
      { headerName: 'Min staff', field: 'minStaff', editable: true, width: 100 },
      {
        headerName: 'Status',
        field: 'active',
        width: 110,
        cellRenderer: (p: any) => (
          <span className={p.value ? 'pill-success' : 'pill-muted'}>
            {p.value ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        pinned: 'right',
        width: 250,
        sortable: false,
        cellRenderer: (p: any) => (
          <div className="flex h-full items-center gap-3 text-xs">
            <button className="text-brand-700 hover:underline" onClick={() => setSelected(p.data.id)}>
              Roster
            </button>
            <button
              className="text-brand-700 hover:underline"
              onClick={() => {
                setEditing(p.data);
                setFormOpen(true);
              }}
            >
              Edit
            </button>
            {p.data.active ? (
              <button
                className="text-rose-600 hover:underline"
                onClick={() => {
                  if (
                    confirm(
                      `Deactivate ${p.data.displayName}? Future scheduled instances will remain until canceled.`,
                    )
                  ) {
                    deactivate.mutate(p.data.id);
                  }
                }}
              >
                Deactivate
              </button>
            ) : (
              <button
                className="text-emerald-700 hover:underline"
                onClick={() => reactivate.mutate(p.data.id)}
              >
                Reactivate
              </button>
            )}
          </div>
        ),
      },
    ],
    [deactivate, reactivate],
  );

  function onCellValueChanged(e: any) {
    if (!e.colDef.editable) return;
    update.mutate({ id: e.data.id, patch: { [e.colDef.field!]: e.newValue } });
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Group sessions</h1>
          <p className="text-sm text-ink-subtle">One row per recurring group track.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button
            className="btn-secondary h-9 whitespace-nowrap"
            onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'groups.csv' })}
          >
            Export CSV
          </button>
          <button
            className="btn-primary h-9 whitespace-nowrap"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Icon name="user-plus" size={14} /> New group
          </button>
        </div>
      </header>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        <AgGridReact
          ref={gridRef}
          rowData={sessions ?? []}
          columnDefs={cols}
          animateRows
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          onCellValueChanged={onCellValueChanged}
          overlayLoadingTemplate={isLoading ? 'Loading…' : ''}
        />
      </div>

      <GroupForm open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
      {selected && <GroupRosterDrawer id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function GroupRosterDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const today = todayLocalIso();
  const { data } = useQuery({
    queryKey: ['roster', id, today],
    queryFn: () =>
      api.get(`/group-sessions/${id}/roster`, { params: { date: today } }).then((r) => r.data),
  });
  return (
    <div className="fixed inset-0 z-30 bg-ink/40" onClick={onClose}>
      <div
        className="absolute right-0 top-0 h-full w-[640px] max-w-[92vw] overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-line-subtle px-5 py-3">
          <h2 className="text-base font-semibold">{data?.session?.displayName ?? 'Roster'}</h2>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-md text-ink-subtle hover:bg-surface-muted"
            aria-label="Close"
          >
            <Icon name="x" size={16} />
          </button>
        </header>
        {data && (
          <div className="p-5">
            <p className="text-xs text-ink-subtle">
              {data.date} ({data.dayOfWeek}) · {fmtTime(data.session.startMin)}–
              {fmtTime(data.session.endMin)} · {data.session.placeOfService}
            </p>
            <p className="mt-1 text-sm text-ink">
              {data.rows.length} / {data.session.maxClients} enrolled today
            </p>
            <ul className="mt-4 divide-y divide-line-subtle">
              {data.rows.map((r: any) => (
                <li
                  key={r.clientId}
                  className="flex items-center justify-between gap-2 py-2 text-sm"
                >
                  <span className="font-mono text-xs text-brand-700">{r.cohortId}</span>
                  <span className="flex-1 truncate">{r.name}</span>
                  <span className="pill-muted">{r.status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
